import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, API } from "../lib/api";
import { LogOut, RefreshCw, Filter, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "", label: "All types" },
  { value: "service", label: "Service" },
  { value: "partnership", label: "Partnership" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "project_management", label: "Project Mgmt" },
  { value: "general", label: "General" },
];

const TYPE_LABEL = {
  service: "Service", partnership: "Partnership", subcontractor: "Subcontractor",
  project_management: "Project Mgmt", general: "General",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [active, setActive] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (!data.is_admin) { navigate("/admin/login", { replace: true }); return; }
      setUser(data);
    } catch {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    if (!user) checkAuth();
  }, [user, checkAuth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/submissions", { params: type ? { inquiry_type: type } : {} });
      setItems(data);
    } catch (e) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { if (user) load(); }, [user, load]);

  async function logout() {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    navigate("/admin/login", { replace: true });
  }

  async function markStatus(id, status) {
    try {
      await api.patch(`/admin/submissions/${id}`, { status });
      setItems((cur) => cur.map((it) => it.id === id ? { ...it, status } : it));
      if (active?.id === id) setActive({ ...active, status });
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  }

  if (!user) {
    return <div className="pt-32 text-center text-slate" data-testid="admin-loading">Loading…</div>;
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen" data-testid="page-admin">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-forest font-semibold mb-1">Admin</div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-navy tracking-tight">Submissions</h1>
            <div className="text-slate text-sm mt-1">Signed in as {user.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-stone px-4 py-2 text-sm text-navy hover:border-navy/40" data-testid="admin-refresh"><RefreshCw size={14}/> Refresh</button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-stone px-4 py-2 text-sm text-navy hover:border-navy/40" data-testid="admin-logout"><LogOut size={14}/> Sign out</button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <Filter size={14} className="text-slate"/>
          <div className="flex flex-wrap gap-2">
            {TYPES.map(t => (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                data-testid={`admin-filter-${t.value || 'all'}`}
                className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${
                  type === t.value ? "bg-navy text-white border-navy" : "bg-white text-navy border-stone hover:border-navy/40"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-stone rounded-2xl bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone/60 text-navy">
                <tr>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Received</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center text-slate py-10">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-slate py-10" data-testid="admin-empty">No submissions.</td></tr>
                ) : items.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => setActive(it)}
                    data-testid={`admin-row-${it.id}`}
                    className={`border-t border-stone cursor-pointer hover:bg-stone/30 ${active?.id === it.id ? "bg-stone/40" : ""}`}
                  >
                    <td className="px-4 py-3">{TYPE_LABEL[it.inquiry_type] || it.inquiry_type}</td>
                    <td className="px-4 py-3">{it.name}</td>
                    <td className="px-4 py-3 text-slate">{it.email}</td>
                    <td className="px-4 py-3 text-slate">{new Date(it.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        it.status === "resolved" ? "bg-forest text-white"
                        : it.status === "in_review" ? "bg-navy text-white"
                        : "bg-stone text-navy"
                      }`}>
                        {it.status || "new"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-stone rounded-2xl bg-white p-6 h-fit sticky top-24" data-testid="admin-detail">
            {!active ? (
              <div className="text-slate text-sm">Select a submission to see details.</div>
            ) : (
              <>
                <div className="text-xs uppercase tracking-widest text-forest font-semibold">{TYPE_LABEL[active.inquiry_type]}</div>
                <div className="text-xl font-bold text-navy mt-1">{active.name}</div>
                <a href={`mailto:${active.email}`} className="text-slate hover:text-navy text-sm block mt-0.5">{active.email}</a>
                <div className="mt-4 space-y-2 text-sm">
                  {Object.entries(active).filter(([k,v]) => v && !["id","status","created_at","file_ids","inquiry_type","name","email"].includes(k)).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <div className="text-slate w-28 capitalize shrink-0">{k.replaceAll("_"," ")}</div>
                      <div className="text-navy break-words">{String(v)}</div>
                    </div>
                  ))}
                </div>
                {active.file_ids?.length > 0 && (
                  <div className="mt-4 border-t border-stone pt-4">
                    <div className="text-xs uppercase tracking-widest text-slate mb-2">Attachments</div>
                    <ul className="space-y-2">
                      {active.file_ids.map((fid) => (
                        <li key={fid} className="flex items-center gap-2 text-sm">
                          <FileText size={14} className="text-slate"/>
                          <a
                            href={`${API}/admin/files/${fid}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-navy underline decoration-forest underline-offset-2 inline-flex items-center gap-1"
                            data-testid={`admin-file-${fid}`}
                          >
                            Download <Download size={12}/>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-6 flex gap-2 flex-wrap">
                  <button onClick={() => markStatus(active.id, "new")} className="rounded-full border border-stone px-3 py-1.5 text-xs hover:border-navy/40" data-testid="admin-status-new">New</button>
                  <button onClick={() => markStatus(active.id, "in_review")} className="rounded-full border border-stone px-3 py-1.5 text-xs hover:border-navy/40" data-testid="admin-status-review">In review</button>
                  <button onClick={() => markStatus(active.id, "resolved")} className="btn-primary rounded-full px-3 py-1.5 text-xs" data-testid="admin-status-resolved">Resolved</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
